import React, { useState, useEffect, useMemo, useCallback } from 'react';
import APIAgent from '../../agents/apiAgent';
import './AllowedEmailsManager.css';

const ROLE_ICONS = { buyer: '🏠', seller: '💰', agent: '🤝' };

export default function UserSwitcherManager({ onSwitchUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [busyUserId, setBusyUserId] = useState(null);
  const [switchError, setSwitchError] = useState(null);
  const [successId, setSuccessId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await APIAgent.request('/api/admin/user-switch/users', 'GET');
        if (!cancelled) setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      return name.includes(q) || (u.email || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q);
    });
  }, [users, search]);

  const handleSwitch = useCallback(async (userId) => {
    setSwitchError(null);
    setSuccessId(null);
    setBusyUserId(userId);
    try {
      await onSwitchUser(userId);
      setSuccessId(userId);
    } catch (err) {
      setSwitchError(err.message || 'Switch failed');
    } finally {
      setBusyUserId(null);
    }
  }, [onSwitchUser]);

  if (loading) {
    return <div className="admin-shell"><p className="admin-empty">Loading users…</p></div>;
  }

  if (error) {
    return <div className="admin-shell"><p className="admin-error">{error}</p></div>;
  }

  return (
    <div className="admin-shell">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Switch Active User</h2>
        <p className="admin-section-description">
          Impersonate any registered user. Your session will be replaced — sign back in with your admin credentials to return to your account. All switches are logged.
        </p>
      </div>

      {switchError && (
        <div className="admin-error" role="alert" style={{ marginBottom: '1rem' }}>
          {switchError}
        </div>
      )}

      <div className="admin-search-row" style={{ marginBottom: '1rem' }}>
        <input
          className="admin-input"
          type="search"
          placeholder="Search by name, email, or role…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search users"
          style={{ width: '100%' }}
        />
      </div>

      {filteredUsers.length === 0 ? (
        <p className="admin-empty">No users match your search.</p>
      ) : (
        <div className="admin-user-list">
          {filteredUsers.map((u) => (
            <div key={u.id} className="admin-user-row">
              <div className="admin-avatar" aria-hidden="true">
                {ROLE_ICONS[u.role] || (u.first_name ? u.first_name[0].toUpperCase() : '?')}
              </div>
              <div className="admin-user-info">
                <span className="admin-user-name">
                  {u.first_name} {u.last_name}
                </span>
                <span className="admin-user-email">{u.email}</span>
                {u.role && <span className="admin-user-role">{u.role}</span>}
              </div>
              <div className="admin-user-actions">
                {successId === u.id ? (
                  <span className="admin-badge admin-badge--allowed">Switched ✓</span>
                ) : (
                  <button
                    className="admin-btn admin-btn--add"
                    onClick={() => handleSwitch(u.id)}
                    disabled={busyUserId === u.id}
                    title={`Switch session to ${u.first_name} ${u.last_name}`}
                  >
                    {busyUserId === u.id ? 'Switching…' : 'Switch to'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
